import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
  DevicesStore,
  InstallationFeaturesActions,
  InstallationFeaturesHelpers,
  InstallationFeaturesStore,
  RoomTypes,
} from '@store';
import {
  DeviceIds,
  Feature,
  FeatureCommands,
  FeaturePropertyKey,
  RoomCommands,
  RoomProperties,
  RoomPropertiesType,
} from '@vi-iot/client';
import { Observable, Subject, from } from 'rxjs';
import {
  combineAll,
  defaultIfEmpty,
  delay,
  map,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { consecutiveSpaceValidator } from 'src/app/shared/validators/consecutive-spaces/consecutive-spaces.validator';
import {
  ImmediateStateMatcher,
  noWhitespaceValidator,
  uniqueNameValidator,
} from '../../../../../shared/common';
import { NoSpecialCharactersValidator } from '../../../../../shared/validators/special-characters/special-characters.validator';
import { InstallationService } from '../../../../services/installation.service';
import { CloseEventDialog } from '../commissioning-delete-device-confirm-modal/commissioning-delete-device-confirm-modal.component';
import { CommissioningDeleteRoomConfirmModalComponent } from '../commissioning-delete-room-confirm-modal/commissioning-delete-room-confirm-modal.component';
import { CommissioningService } from '../commissioning.service';

interface EditRoomFormGroup {
  roomName: FormControl<string>;
  roomType: FormControl<RoomPropertiesType>;
}

@Component({
  selector: 'app-commissioning-edit-room-modal',
  templateUrl: './commissioning-edit-room-modal.component.html',
  styleUrls: ['./commissioning-edit-room-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningEditRoomModalComponent implements OnInit, OnDestroy {
  allRoomNames: string[] = [];
  form: FormGroup<EditRoomFormGroup>;
  roomTypeOptions = Object.keys(RoomTypes);
  roomTypes = RoomTypes;
  matcher = new ImmediateStateMatcher();
  canDeleteRoom$: Observable<boolean>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public dialogRef: MatDialogRef<CommissioningEditRoomModalComponent>,
    private installationService: InstallationService,
    private actions$: Actions,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: { room: Feature<RoomProperties, RoomCommands>; isOtherRoom?: boolean },
    private dialog: MatDialog,
    private commissioningService: CommissioningService,
    private installationFeaturesStore: InstallationFeaturesStore,
    public devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('520px');

    this.initRoomNames();
    this.initFormGroup();

    this.canDeleteRoom$ = this.devicesStore.isSelectedDeviceOBNG$.pipe(
      map((isObng) => !isObng || InstallationFeaturesHelpers.canRemoveRoom(this.data.room))
    );
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  onDeleteRoom(): void {
    const dialogRef = this.dialog.open(CommissioningDeleteRoomConfirmModalComponent, {
      width: '715px',
      panelClass: 'commissioning-delete-room-confirm-dialog',
      data: {
        room: this.data.room,
      },
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        this.commissioningService.deleteRoom(this.data.room);

        if (event.shouldDeactivateSRC) {
          this.commissioningService.deactivateSRC();
        }

        this.dialogRef.close();
      }
    });
  }

  onConfirm(): void {
    const roomFeature = this.data.room.feature;

    const newRoomName = this.form.get('roomName').value.trim();
    const newRoomType = this.form.get('roomType').value;

    const isRoomNameChanged = this.isRoomNameChanged(newRoomName);
    const isRoomTypeChanged = this.isRoomTypeChanged(newRoomType);

    if (this.data.isOtherRoom && isRoomNameChanged) {
      this.updateRoomName(newRoomName, roomFeature);
    } else {
      if (isRoomNameChanged && isRoomTypeChanged) {
        this.updateRoomAndType(newRoomName, newRoomType, roomFeature);
      } else if (isRoomNameChanged) {
        this.updateRoomName(newRoomName, roomFeature);
      } else if (isRoomTypeChanged) {
        this.updateRoomType(newRoomType, roomFeature);
      }
    }

    this.dialogRef.close();
  }

  isRoomNameChanged(roomName: string): boolean {
    return (
      roomName.toLocaleLowerCase() !==
      (this.data.room?.properties?.name?.value || '').toLocaleLowerCase()
    );
  }

  isRoomTypeChanged(roomType: RoomPropertiesType): boolean {
    return roomType !== this.data.room?.properties?.type?.value;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  isConfirmButtonDisabled(): boolean {
    if (this.data.isOtherRoom) {
      return !this.isRoomNameChanged(this.form.get('roomName').value.trim()) || !this.form.valid;
    } else {
      return (
        (!this.isRoomTypeChanged(this.form.get('roomType').value) &&
          !this.isRoomNameChanged(this.form.get('roomName').value.trim())) ||
        !this.form.valid
      );
    }
  }

  getTitle(): string {
    return this.data.isOtherRoom
      ? this.translate.instant('+SRC_COMMISSIONINGV2.EDIT_OTHER_ROOM_MODAL_TITLE_SECONDARY')
      : this.translate.instant('+SRC_COMMISSIONINGV2.EDIT_ROOM_MODAL_TITLE_SECONDARY');
  }

  private initFormGroup(): void {
    this.form = new FormGroup<EditRoomFormGroup>({
      roomName: new FormControl(this.data.room?.properties?.name?.value || '', [
        Validators.required,
        noWhitespaceValidator(),
        Validators.maxLength(40),
        uniqueNameValidator(this.allRoomNames),
        NoSpecialCharactersValidator(),
        consecutiveSpaceValidator(),
      ]),
      roomType: new FormControl<RoomPropertiesType>(
        this.data.room?.properties?.type?.value,
        this.data.isOtherRoom ? [] : [Validators.required]
      ),
    });
  }

  private initRoomNames(): void {
    this.installationFeaturesStore.notAssignedHCsToOtherRooms$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((data) => {
        const unassignedHCNames = from(data).pipe(
          map((unassignedHC) => this.installationService.getCircuitName(unassignedHC, true)),
          combineAll(),
          defaultIfEmpty([]) // Please, be aware that HCs does not always emit, i.e. for standalone
        );

        this.installationFeaturesStore.roomsAndOtherRoomsNames$
          .pipe(takeUntil(this.ngUnsubscribe$), withLatestFrom(unassignedHCNames))
          .subscribe(([allRoomNames, unassignedHCsToOtherRoomNames]) => {
            const allNames = [...allRoomNames, ...unassignedHCsToOtherRoomNames];
            this.allRoomNames = allNames.filter(
              (roomName) => roomName !== this.data.room?.properties?.name?.value
            );
          });
      });
  }

  private updateRoomAndType(roomName: string, roomType: RoomPropertiesType, feature: string): void {
    this.actions$
      .pipe(ofType(InstallationFeaturesActions.updateFeature.success), take(1), delay(1000))
      .subscribe(() => {
        this.updateRoomType(roomType, feature);
      });
    this.updateRoomName(roomName, feature, true);
  }

  private updateRoomName(roomName: string, feature: string, hideSuccessSnackbar = false): void {
    // Refresh delay to avoid LU bugs
    this.installationFeaturesStore.updateFeature(
      feature,
      roomName,
      null,
      FeaturePropertyKey.name,
      FeatureCommands.setName,
      null,
      DeviceIds.RoomControl,
      {
        hideSuccessSnackbar,
      }
    );
  }

  private updateRoomType(roomType: RoomPropertiesType, feature: string): void {
    this.installationFeaturesStore.updateFeature(
      feature,
      roomType,
      null,
      FeaturePropertyKey.type,
      FeatureCommands.setType,
      null,
      DeviceIds.RoomControl
    );
  }
}
